// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for managing and combining different types of asset timeline events.
 * This module provides functionality to fetch, merge, and sort various types of
 * asset-related events including ownership changes, event groups, and individual
 * events into a unified timeline.
 */
import { getAssetOwnership } from '@/asset/api/get-asset-ownership';
import { getEventGroups } from '@/asset/api/get-event-groups';
import { getEvents } from '@/asset/api/get-events';
import { AssetOwnershipEvent, Event, EventGroup } from '@/asset/api/types';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * Union type representing any event that can appear in an asset's timeline.
 * Includes ownership changes, event groups, and individual events.
 */
type TimelineEvent = EventGroup | Event | AssetOwnershipEvent;

/**
 * Type guard to identify ownership events in the timeline.
 * Checks for the presence of the 'customer' property which is unique
 * to ownership events.
 *
 * @param event - Event to check
 * @returns True if the event is an ownership event
 */
function isAssetOwnershipEvent(
  event: TimelineEvent
): event is AssetOwnershipEvent {
  return 'customer' in event;
}

/**
 * Extracts the DateTime from any type of timeline event.
 * Handles the different date field names used by each event type:
 * - Event groups use minimumDate
 * - Ownership events use date
 * - Individual events use occurredAt
 *
 * @param event - Timeline event to get the date from
 * @returns DateTime of the event, or null if no date is available
 */
function getTimelineEventDateTime(event: TimelineEvent) {
  if ('events' in event) {
    return event.minimumDate;
  }

  if (isAssetOwnershipEvent(event)) {
    return event.date;
  }

  return event.occurredAt;
}

/**
 * Compares two timeline events for chronological sorting.
 * Events without dates are sorted after events with dates.
 * For ownership events with the same date, uses lastModifiedAt
 * as a secondary sort key to maintain consistent ordering.
 *
 * @param event - First event to compare
 * @param other - Second event to compare
 * @returns Negative if event is later, positive if other is later
 */
function compareTimelineEvents(event: TimelineEvent, other: TimelineEvent) {
  const eventDateTime = getTimelineEventDateTime(event);
  const otherDateTime = getTimelineEventDateTime(other);

  if (eventDateTime === null) {
    return 1;
  }

  if (otherDateTime === null) {
    return -1;
  }

  const difference = otherDateTime.toMillis() - eventDateTime.toMillis();

  if (
    difference === 0 &&
    isAssetOwnershipEvent(event) &&
    isAssetOwnershipEvent(other)
  ) {
    return other.lastModifiedAt.toMillis() - event.lastModifiedAt.toMillis();
  }

  return difference;
}

/**
 * Combines event groups and individual events into a single timeline.
 * Filters out individual events that are already part of an event group
 * to prevent duplicate entries in the timeline.
 *
 * @param eventGroups - Event groups to include in the timeline
 * @param events - Individual events to potentially include
 * @returns Combined array of events and event groups
 */
function getTimelineData(eventGroups: EventGroup[], events: Event[]) {
  const data: (Event | EventGroup)[] = eventGroups.slice();

  events.forEach((event) => {
    const isGrouped = eventGroups.some((eventGroup) =>
      eventGroup.events.some(({ id }) => id === event.id)
    );

    if (isGrouped) {
      return;
    }

    data.push(event);
  });

  return data;
}

/**
 * A React hook that fetches and combines all events related to an asset.
 * Retrieves ownership events, event groups, and individual events, then
 * combines them into a single chronologically sorted timeline. Events that
 * are part of a group are only included within their group to prevent
 * duplication.
 *
 * Uses React Query's suspense mode for loading states and automatic
 * background revalidation. The hook will suspend the component while
 * the initial data is being fetched.
 *
 * @param key - Unique identifier of the asset to fetch events for
 * @returns Array of timeline events sorted chronologically (newest first)
 * @throws Will throw a promise during initial load (for Suspense)
 * @throws Will throw an error if any API request fails or if response data is invalid
 */
export function useAssetActions(key: string) {
  const eventsQuery = useSuspenseQuery({
    queryKey: ['asset', key, 'event'],
    queryFn: () => getEvents(key),
  });

  const eventGroupsQuery = useSuspenseQuery({
    queryKey: ['asset', key, 'event-group'],
    queryFn: () => getEventGroups(key),
  });

  const ownershipQuery = useSuspenseQuery({
    queryKey: ['asset', key, 'ownership'],
    queryFn: () => getAssetOwnership(key),
  });

  const events = [
    ...ownershipQuery.data.events,
    ...getTimelineData(
      eventGroupsQuery.data.eventGroups,
      eventsQuery.data.events
    ),
  ];

  return events.sort(compareTimelineEvents);
}
